import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Boxes, RefreshCw, AlertTriangle, TrendingUp, TrendingDown } from "lucide-react";

const METHODS = [
  { v: "LATEST", label: "Ultimo costo" },
  { v: "WEIGHTED_AVERAGE", label: "Media ponderata" },
  { v: "PERIOD_WEIGHTED_AVERAGE", label: "Media ponderata periodo" },
];
const money = (n, dp = 5) => (n == null ? "—" : `€ ${Number(n).toFixed(dp)}`);

export default function FoodCostIngredienti() {
  const [rows, setRows] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fc/valuations");
      setRows(data.items || []);
      setCfg(data.settings || null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveCfg = async (patch) => {
    try {
      const { data } = await api.put("/fc/costing/settings", patch);
      setCfg(data); toast.success("Impostazioni aggiornate"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const rebuild = async () => {
    try { const { data } = await api.post("/fc/costing/rebuild-history"); toast.success(`Storico: ${data.history_records} record`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="fc-ingredienti-page">
      <PageHeader title="Food Cost · Ingredienti" subtitle="Valorizzazione ingredienti dallo storico fatture reali" testid="fc-ing-header" />

      {cfg && (
        <Card className="p-5 rounded-xl mb-5 flex flex-wrap items-end gap-4" data-testid="fc-costing-settings">
          <div className="space-y-1.5">
            <Label>Metodo valorizzazione</Label>
            <Select value={cfg.valuation_method} onValueChange={(v) => saveCfg({ valuation_method: v })}>
              <SelectTrigger className="w-56" data-testid="fc-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {METHODS.map((m) => <SelectItem key={m.v} value={m.v} data-testid={`fc-method-${m.v}`}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Periodo (giorni)</Label>
            <Input type="number" className="w-28" defaultValue={cfg.period_days}
              onBlur={(e) => Number(e.target.value) !== cfg.period_days && saveCfg({ period_days: Number(e.target.value) })}
              data-testid="fc-period-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Soglia FC alto (%)</Label>
            <Input type="number" className="w-28" defaultValue={cfg.food_cost_high_threshold}
              onBlur={(e) => Number(e.target.value) !== cfg.food_cost_high_threshold && saveCfg({ food_cost_high_threshold: Number(e.target.value) })}
              data-testid="fc-threshold-input" />
          </div>
          <div className="space-y-1.5">
            <Label>IVA vendita globale (%)</Label>
            <Input type="number" className="w-28" defaultValue={cfg.sales_vat_rate}
              onBlur={(e) => Number(e.target.value) !== cfg.sales_vat_rate && saveCfg({ sales_vat_rate: Number(e.target.value) })}
              data-testid="fc-salesvat-input" />
          </div>
          <Button variant="outline" className="rounded-full ml-auto" onClick={rebuild} data-testid="fc-rebuild-history">
            <RefreshCw className="w-4 h-4 mr-1" /> Ricostruisci storico
          </Button>
        </Card>
      )}

      <Card className="p-5 rounded-xl" data-testid="fc-valuations-card">
        {loading ? (
          <p className="text-muted-foreground py-8 text-center">Caricamento…</p>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground" data-testid="fc-val-empty">
            <Boxes className="w-10 h-10 mx-auto mb-3 text-primary/60" />
            Nessun ingrediente. Crea gli ingredienti dalla pagina "Prodotti da associare".
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                <th className="py-2 px-2">Ingrediente</th>
                <th className="py-2 px-2 text-right">Ultimo costo</th>
                <th className="py-2 px-2 text-right">Media ponderata</th>
                <th className="py-2 px-2 text-right">Costo food cost</th>
                <th className="py-2 px-2 text-right">Var. %</th>
                <th className="py-2 px-2">Fornitore</th>
                <th className="py-2 px-2">Ultimo acquisto</th>
                <th className="py-2 px-2 text-center">Stato</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.ingredient_id} className="border-b border-border/60 last:border-0" data-testid={`fc-val-row-${r.ingredient_id}`}>
                    <td className="py-2 px-2 font-medium">{r.canonical_name}<span className="text-muted-foreground"> · /{r.base_unit}</span></td>
                    <td className="py-2 px-2 text-right">{money(r.last_cost)}</td>
                    <td className="py-2 px-2 text-right">{money(r.weighted_avg)}</td>
                    <td className="py-2 px-2 text-right font-semibold">{money(r.cost_used)}</td>
                    <td className={`py-2 px-2 text-right ${r.pct_change > 0 ? "text-destructive" : r.pct_change < 0 ? "text-[hsl(var(--success))]" : "text-muted-foreground"}`}>
                      {r.pct_change == null ? "—" : (<span className="inline-flex items-center gap-0.5">{r.pct_change > 0 ? <TrendingUp className="w-3 h-3" /> : r.pct_change < 0 ? <TrendingDown className="w-3 h-3" /> : null}{r.pct_change > 0 ? "+" : ""}{r.pct_change}%</span>)}
                    </td>
                    <td className="py-2 px-2 text-muted-foreground">{r.last_supplier || "—"}</td>
                    <td className="py-2 px-2 text-muted-foreground">{r.last_purchase_date || "—"}</td>
                    <td className="py-2 px-2 text-center">
                      {r.anomaly ? (
                        <span title={(r.anomaly_reasons || []).join(", ")} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600" data-testid={`fc-val-anomaly-${r.ingredient_id}`}>
                          <AlertTriangle className="w-3 h-3" /> Da verificare
                        </span>
                      ) : r.cost_used == null ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">No storico</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]">OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
